import { useSelector } from "react-redux";
import { useContext } from "react";

import { getIsBundleShop, getIsLockerShop } from "@selectors/store";

import { isShippingDetailRequired } from "@helpers/product-details";
import { isSwagProduct } from "@helpers/products/types";

import Multimedia from "@sharedComponents/multimedia";
import StadiumShippedTooltip from "@stadium/ui/src/components/stadium-shipped-tooltip";
import ToolTip from "@stadium/ui/src/components/tooltip/tooltip";
import Accordion from "@stadium/ui/src/components/accordion/accordion";

import { CurrentDeviceContext } from "@stadium/ui/src/contexts/responsive-content";

import coreOnDemandImg from "@stadium/ui/src/assets/images/core-on-demand.svg";
import ecofriendlyImg from "@stadium/ui/src/assets/images/eco-friendly.svg";
import sustainableImg from "@stadium/ui/src/assets/images/sustainable.svg";

import { CORE_ON_DEMAND_TEXT } from "@constants/products/listing";
import { HELP_CENTER_SWAG_DECORATION_METHODS_ARTICLE } from "@constants/external-routes";

import { ExternalButton } from "@sharedComponents/buttons";

import embroideryImg from "@assets/images/svg/embroidery.svg";

import styles from "./main.module.scss";

const ProductInfoSection = ({ product, measurementInfo }) => {
  const { isMobileDevice } = useContext(CurrentDeviceContext);

  const isBundleShop = useSelector(getIsBundleShop);
  const isLockerShop = useSelector(getIsLockerShop);

  const { ecoFriendly, sustainable, shippedFromStadium, coreOnDemand, servingInfo } = product || {};

  const showCoreOnDemandBadge = coreOnDemand && isLockerShop;

  const showBadge = Boolean(
    ecoFriendly ||
      sustainable ||
      shippedFromStadium ||
      showCoreOnDemandBadge ||
      !!product?.storeProduct?.embroidery,
  );

  const shippingDetailRequired = isShippingDetailRequired(product.intent);
  const isSwag = isSwagProduct(product.intent);

  const renderShippingAndProductionDetails = () => (
    <div className={styles.productAddInfoCont}>
      {isBundleShop
        ? ""
        : (shippingDetailRequired || servingInfo || measurementInfo) && (
            <div className={styles.addInfoRow}>
              {shippingDetailRequired && (
                <div className={styles.shippingText}>
                  <i className={styles.iconShipping}></i>
                  {isSwag ? (
                    <div>
                      One print location, basic decoration, and shipping included.{" "}
                      <ExternalButton
                        href={HELP_CENTER_SWAG_DECORATION_METHODS_ARTICLE}
                        styleType="link"
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.shippingLearnMoreLink}
                        prefetch={false}
                        aria-label="Learn more about print locations, decoration, and shipping (opens in new tab)"
                      >
                        <span className={styles.shippingLearnMoreLabel}>Learn more</span>
                        <span className={styles.shippingLearnMoreExternalIcon} aria-hidden />
                      </ExternalButton>
                    </div>
                  ) : (
                    "Shipping Included"
                  )}
                </div>
              )}
              {servingInfo && (
                <div className={styles.serveText}>
                  <span className={styles.divider}>|</span>
                  <i className={styles.iconServe} />
                  SERVES {servingInfo}
                </div>
              )}
              {measurementInfo && (
                <div className={styles.serveText}>
                  <span className={styles.divider}>|</span>
                  {measurementInfo}
                </div>
              )}
            </div>
          )}
      {showBadge && (
        <>
          {!!shippedFromStadium && (
            <div className={styles.addInfoRow}>
              <StadiumShippedTooltip
                withHeading
                asResponsiveModal
                contentWrapperClassName={styles.toolTipWrapCont}
              >
                <div className={styles.qualityTitleIconWrap}>
                  <div className={styles.qualityTitle}>Ships from Stadium Warehouse</div>
                </div>
              </StadiumShippedTooltip>
            </div>
          )}
          {product?.storeProduct?.embroidery && (
            <div className={styles.addInfoRow}>
              <div className={styles.qualityList}>
                <ToolTip
                  containerClass={`${styles.toolTipWrapper} ${styles.embroideryTipWrapper}`}
                  heading="Embroidery"
                  isLightTheme
                  placement="top-start"
                  arrow={true}
                  asResponsiveModal
                >
                  <div className={styles.toolTipWrapCont}>
                    <Multimedia
                      type="image"
                      src={embroideryImg}
                      height={16}
                      isNextImage={false}
                      className={styles.qualityImg}
                    />
                    <div className={styles.qualityTitle}>Embroidery</div>
                  </div>
                </ToolTip>
              </div>
            </div>
          )}
          {(ecoFriendly || sustainable) && (
            <div className={styles.addInfoRow}>
              <div className={styles.productQualityCont}>
                {!!sustainable && (
                  <div className={styles.qualityList}>
                    <ToolTip
                      containerClass={styles.toolTipWrapper}
                      heading="Sustainable Printing"
                      content="We only print once ordered, which reduces waste and preserves resources."
                      isLightTheme
                      placement="top-start"
                      arrow={true}
                      asResponsiveModal
                    >
                      <div className={styles.toolTipWrapCont}>
                        <Multimedia
                          type="image"
                          src={sustainableImg}
                          height={16}
                          isNextImage={false}
                          className={styles.qualityImg}
                        />
                        <div className={styles.qualityTitle}>Sustainable printing</div>
                      </div>
                    </ToolTip>
                  </div>
                )}
                {!!ecoFriendly && (
                  <div className={styles.qualityList}>
                    <ToolTip
                      containerClass={styles.toolTipWrapper}
                      heading="Eco-friendly"
                      content="This item is crafted from sustainable and recycled materials, reducing carbon footprint."
                      isLightTheme
                      placement="top-start"
                      arrow={true}
                      asResponsiveModal
                    >
                      <div className={styles.toolTipWrapCont}>
                        <Multimedia
                          type="image"
                          src={ecofriendlyImg}
                          height={16}
                          isNextImage={false}
                          className={styles.qualityImg}
                        />
                        <div className={styles.qualityTitle}>Eco-friendly materials</div>
                      </div>
                    </ToolTip>
                  </div>
                )}
              </div>
            </div>
          )}
          {showCoreOnDemandBadge && (
            <div className={styles.addInfoRow}>
              <div className={styles.qualityList}>
                <ToolTip
                  containerClass={styles.toolTipWrapper}
                  heading="Core On-Demand"
                  content={CORE_ON_DEMAND_TEXT}
                  isLightTheme
                  placement="top-start"
                  arrow={true}
                  asResponsiveModal
                  contentContainerClass={styles.tippyContent}
                >
                  <div className={styles.toolTipWrapCont}>
                    <Multimedia
                      type="image"
                      src={coreOnDemandImg}
                      height={16}
                      isNextImage={false}
                      className={styles.qualityImg}
                    />
                    <div className={styles.qualityTitle}>Core On-demand item</div>
                  </div>
                </ToolTip>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );

  const renderShippingAndProductionDetailsAccordion = () => {
    if (isMobileDevice) {
      const showShippingAndProductionDetails =
        (!isBundleShop && (shippingDetailRequired || servingInfo || measurementInfo)) ||
        (showBadge &&
          (shippedFromStadium || product?.storeProduct?.embroidery || ecoFriendly || sustainable));

      if (!showShippingAndProductionDetails) return null;

      return (
        <Accordion
          id="shipping-and-production-details"
          customClass={styles.panelGroup}
          titleContent={
            <div className={styles.acord}>
              <div className={styles.primaryColor}>Shipping & Production Details</div>
              <div className={`${styles.iconChevronUp} collapseicon`}></div>
            </div>
          }
        >
          {renderShippingAndProductionDetails()}
        </Accordion>
      );
    }

    return renderShippingAndProductionDetails();
  };

  return (
    <div className={styles.sharedInfoWithFlag} data-group="accordion">
      {renderShippingAndProductionDetailsAccordion()}
    </div>
  );
};

export default ProductInfoSection;
